import { isProjectDestination, type ProjectDestination } from "./workspaces/project/projectNavigation";
export const BUSINESS_SECTIONS = ["overview", "expenses", "accounts", "loans", "assets", "reports", "ledger-accounts", "journal", "review", "reconcile", "books"] as const;
export type BusinessSection = typeof BUSINESS_SECTIONS[number];
export const isBusinessSection = (value: string): value is BusinessSection => (BUSINESS_SECTIONS as readonly string[]).includes(value);
export type AppRoute =
  | { [K in "projects" | "portfolio" | "find" | "admin" | "account" | "billing" | "standards" | "privacy"]: { view: K } }["projects" | "portfolio" | "find" | "admin" | "account" | "billing" | "standards" | "privacy"]
  | { view: "project" | "shared"; projectId: string; destination?: ProjectDestination; focusId?: string }
  | { view: "deals"; dealId?: string }
  | { view: "business"; section?: BusinessSection; focusId?: string };

const SIMPLE = new Set(["projects", "portfolio", "find", "admin", "account", "billing", "standards", "privacy"]);
export function parseAppRoute(hash: string): AppRoute {
  try {
    const [path, query = ""] = hash.replace(/^#\/?/, "").split("?");
    const parts = path.split("/").map(decodeURIComponent);
    if (parts[0] === "business") {
      const focusId = new URLSearchParams(query).get("focus");
      return { view: "business", section: isBusinessSection(parts[1] ?? "") ? parts[1] as BusinessSection : "overview", ...(focusId ? { focusId } : {}) };
    }
    if (parts[0] === "project" || parts[0] === "shared") {
      if (!parts[1]) return { view: "projects" };
      const destination = isProjectDestination(parts[2] ?? "") ? parts[2] as ProjectDestination : "reports";
      return { view: parts[0], projectId: parts[1], destination, ...(new URLSearchParams(query).get("focus") ? { focusId: new URLSearchParams(query).get("focus")! } : {}) };
    }
    if (parts[0] === "deals") return { view: "deals", ...(parts[1] ? { dealId: parts[1] } : {}) };
    return SIMPLE.has(parts[0]) ? { view: parts[0] } as AppRoute : { view: "projects" };
  } catch { return { view: "projects" }; }
}
export function appRouteHash(route: AppRoute): string {
  if (route.view === "business") return `#business/${route.section ?? "overview"}${route.focusId ? `?${new URLSearchParams({ focus: route.focusId })}` : ""}`;
  if (route.view === "project" || route.view === "shared") {
    const query = route.focusId ? `?${new URLSearchParams({ focus: route.focusId })}` : "";
    return `#${route.view}/${encodeURIComponent(route.projectId)}/${route.destination ?? "reports"}${query}`;
  }
  if (route.view === "deals" && route.dealId) return `#deals/${encodeURIComponent(route.dealId)}`;
  return `#${route.view}`;
}
